import { useTranslation } from 'react-i18next';
import { useMutation } from 'react-query';
import { useNavigate } from 'react-router-dom';

import { Button } from 'primereact/button';
import PropTypes from 'prop-types';
import { Dialog } from 'primereact/dialog';
import { Message } from 'primereact/message';
import { useEffect, useState } from 'react';
import { classNames } from 'primereact/utils';
import ReactRouterPrompt from 'react-router-prompt';
import moment from 'moment';
import { submitLiquidation, downloadLiquidationFile } from 'apis/liquidation.api';
import { toast } from 'layout';
import { TOAST } from 'constant';
import { DatePicker, TextInput, CurrencyInput, FileInput } from 'components/FormControl';
import { formatFileName, downloadOrViewPdfFile } from 'utils/func';
import { Can } from 'components/Can';
import UploadMultipleFile from '../UploadMultipleFile';

function LiquidationForm({
  control,
  errors,
  handleSubmit,
  refetch,
  checkKeyDown,
  footerConfirmationPrompt,
  isDirty,
  dirtyFields,
  setValue,
  liquidation,
  appendixFilesDelete,
  setAppendixFilesDelete,
  isCompleted,
  readOnly,
}) {
  // #region Data
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [isSubmitModalOpen, setIsSubmitModalOpen] = useState(false);
  const [appendixeArray, setAppendixeArray] = useState([]);

  const { mutate: submitLiquidationMutate, isLoading: isSubmitLiquidationLoading } =
    useMutation(submitLiquidation);

  const { mutateAsync: mutateAsyncDownloadFile } = useMutation(downloadLiquidationFile);

  const handleSubmitContract = () => {
    submitLiquidationMutate(liquidation?.id, {
      onSuccess: () => {
        toast(TOAST.SUCCESS, t('liquidation.liquidatedSuccessfully'));
        refetch();
      },
      onError: () => {
        toast(TOAST.ERROR, 'Xảy ra lỗi, vui lòng thử lại');
      },
    });
  };

  const handleDownloadFile = async (opts, fileNameFormat, isView) => {
    try {
      const res = await mutateAsyncDownloadFile({ path: opts.path });
      if (res?.data) {
        const file = new Blob([res.data], { type: 'application/pdf' });
        const filename =
          res.headers['content-disposition']?.split('filename=')?.[1] ||
          fileNameFormat ||
          'file.pdf';
        downloadOrViewPdfFile(file, filename, isView);
      }
    } catch (error) {
      if (isView) {
        toast(TOAST.ERROR, t('projectProposal.review.viewPdfErrorTitle'));
      } else {
        toast(TOAST.ERROR, t('projectProposal.review.exportPdfErrorTitle'));
      }
    }
  };

  const footerConfirmationSubmit = () => (
    <div>
      <Button
        label={t('projectProposal.cancel')}
        className="w-7rem"
        severity="danger"
        onClick={() => setIsSubmitModalOpen(false)}
        autoFocus
      />
      <Button
        label={t('projectProposal.ok')}
        className="w-7rem"
        severity="info"
        onClick={() => {
          handleSubmitContract();
          setIsSubmitModalOpen(false);
        }}
      />
    </div>
  );

  useEffect(() => {
    setAppendixeArray(liquidation?.liquidationAppendices ?? []);
  }, [liquidation]);

  useEffect(() => {
    setValue('projectAppendixDeletedFileIds', appendixFilesDelete ?? []);
  }, [appendixFilesDelete]);

  const isDeleteAppendixFileIdsChanged = appendixFilesDelete?.length > 0;

  return (
    <div className="p-5">
      <Dialog
        header={<p className="text-blue-500">{t('projectProposal.confirmationTitle')}</p>}
        visible={isSubmitModalOpen}
        position="center"
        style={{ width: '400px' }}
        onHide={() => setIsSubmitModalOpen(false)}
        draggable={false}
        footer={footerConfirmationSubmit}
      >
        <Message
          className="mt-4 w-full"
          severity="warn"
          text={t('liquidation.confirmationSubmitMessage')}
        />
      </Dialog>
      {/* eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions */}
      <form onSubmit={handleSubmit} onKeyDown={checkKeyDown} autoComplete="off">
        <div className="relative">
          <h4 className="text-center mb-4">{t('projectProposal.general.label')}</h4>
          <div className="grid p-fluid form-table">
            <div className="col-4 my-2">
              {readOnly ? (
                <TextInput
                  label={t('liquidation.liquidationDate')}
                  name="liquidationDateDefault"
                  control={control}
                  errors={errors}
                  isRequired
                  readOnly={readOnly}
                  defaultValue={moment(liquidation?.liquidationDate).format('DD/MM/YYYY')}
                />
              ) : (
                <DatePicker
                  label={t('liquidation.liquidationDate')}
                  name="liquidationDate"
                  control={control}
                  errors={errors}
                  isRequired
                  disabled={readOnly}
                />
              )}
            </div>
            <div className="col-4 my-2">
              <TextInput
                label={t('liquidation.memorandumCode')}
                name="code"
                control={control}
                errors={errors}
                isRequired
                readOnly={readOnly}
              />
            </div>
            <div className="col-4 my-2">
              <div className="flex flex-column">
                <label>{t('liquidation.status')}</label>
                <p className="py-3 font-bold">
                  {isCompleted ? t('council.m06.b.reach') : t('council.m06.b.failed')}
                </p>
              </div>
            </div>

            {/* <h4 className="text-primary">Xử lý tài chính</h4> */}
            <table className="mb-2 col-12">
              <thead>
                <tr>
                  <th className="text-center" style={{ minWidth: 260, width: 360 }}>
                    {t('projectProposal.expenditure.label')}
                  </th>
                  <th className="text-center" style={{ minWidth: 130, width: 360 }}>
                    {t('projectProposal.expenditure.expenditure.total')}
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td className="font-bold">{t('liquidation.table.vnuExpenditure')}</td>
                  <td>
                    <CurrencyInput
                      name="vnuExpenditure"
                      control={control}
                      errors={errors}
                      readOnly={readOnly}
                    />
                  </td>
                </tr>
                {isCompleted ? (
                  <tr>
                    <td className="font-bold">{t('liquidation.table.suggestedExpenditure')}</td>
                    <td>
                      <CurrencyInput
                        name="suggestedExpenditure"
                        control={control}
                        errors={errors}
                        readOnly={readOnly}
                      />
                    </td>
                  </tr>
                ) : (
                  <>
                    <tr>
                      <td className="font-bold">{t('liquidation.table.usedExpenditure')}</td>
                      <td>
                        <CurrencyInput
                          name="usedExpenditure"
                          control={control}
                          errors={errors}
                          readOnly={readOnly}
                        />
                      </td>
                    </tr>
                    <tr>
                      <td className="font-bold">{t('liquidation.table.acceptedExpenditure')}</td>
                      <td>
                        <CurrencyInput
                          name="acceptedExpenditure"
                          control={control}
                          errors={errors}
                          readOnly={readOnly}
                        />
                      </td>
                    </tr>
                    <tr>
                      <td className="font-bold">
                        {t('liquidation.table.stateReimbursementExpenditure')}
                      </td>
                      <td>
                        <CurrencyInput
                          name="stateReimbursementExpenditure"
                          control={control}
                          errors={errors}
                          readOnly={readOnly}
                        />
                      </td>
                    </tr>
                  </>
                )}
              </tbody>
            </table>

            {/* <div className="col-12 my-2">
              <TextareaInput
                label="Ghi chú"
                name="notes"
                control={control}
                errors={errors}
                isRequired
                disabled={disabled}
                row={3}
              />
            </div> */}
            <div className="col-12 my-2">
              {readOnly ? (
                <h5>{t('liquidation.decisionFile')}</h5>
              ) : (
                <FileInput
                  label={t('liquidation.decisionFile')}
                  name="liquidationDecisionFile"
                  control={control}
                  errors={errors}
                  accept=".pdf"
                  isRequired
                  disabled={liquidation?.isSentEmail}
                />
              )}

              {liquidation?.decisionFile && (
                <span
                  className="underline "
                  style={{ color: 'blue', cursor: 'pointer' }}
                  onClick={() => handleDownloadFile(liquidation?.decisionFile)}
                >
                  {formatFileName(liquidation?.decisionFile)}
                </span>
              )}
            </div>
            <div className="col-12">
              <UploadMultipleFile
                disabled={readOnly}
                control={control}
                errors={errors}
                appendixeArray={appendixeArray}
                setAppendixFilesDelete={setAppendixFilesDelete}
                setAppendixeArray={setAppendixeArray}
                handleDownloadFile={handleDownloadFile}
              />
            </div>
          </div>

          {!readOnly && (
            <ReactRouterPrompt when={isDirty && Object.keys(dirtyFields)}>
              {({ isActive, onConfirm, onCancel }) => (
                <Dialog
                  visible={isActive}
                  header={<p className="text-blue-500">{t('projectProposal.confirmationTitle')}</p>}
                  position="center"
                  style={{ width: '300px' }}
                  onHide={onCancel}
                  draggable={false}
                  footer={() => footerConfirmationPrompt(onConfirm, onCancel)}
                >
                  <p className="text-lg py-3">{t('projectProposal.confirmationPromptMessage')}</p>
                </Dialog>
              )}
            </ReactRouterPrompt>
          )}
        </div>

        <div className="flex justify-content-end mt-2 gap-4">
          <Button
            className="w-10rem"
            style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}
            label={t('contract.comeBack')}
            comeBack
            severity="danger"
            type="button"
            onClick={() => {
              navigate('/projects-submit');
            }}
          />
          {!readOnly && (
            <Can do="create" on="liquidation">
              <Button
                type="submit"
                disabled={
                  (!(isDirty && Object.keys(dirtyFields).length) &&
                    !isDeleteAppendixFileIdsChanged) ||
                  liquidation?.isSentEmail
                }
                className=" w-10rem "
                style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}
              >
                {t('contract.save')}
              </Button>
              <Button
                type="button"
                className="btn-control w-14rem"
                icon={classNames(
                  'pi mr-1 ',
                  { 'pi-spin pi-spinner': isSubmitLiquidationLoading },
                  { 'pi pi-check': !isSubmitLiquidationLoading }
                )}
                disabled={
                  (isDirty && Object.keys(dirtyFields).length) ||
                  isDeleteAppendixFileIdsChanged ||
                  liquidation?.isSentEmail ||
                  !liquidation?.id
                }
                style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}
                severity="success"
                onClick={() => {
                  setIsSubmitModalOpen(true);
                }}
              >
                {t('liquidation.button')}
              </Button>
            </Can>
          )}
        </div>
      </form>
    </div>
  );
}

LiquidationForm.propTypes = {
  control: PropTypes.shape({}).isRequired,
  errors: PropTypes.shape({}).isRequired,
  handleSubmit: PropTypes.func.isRequired,
  // tab: PropTypes.number.isRequired,
  checkKeyDown: PropTypes.func.isRequired,
  footerConfirmationPrompt: PropTypes.func,
  refetch: PropTypes.func.isRequired,
  // eslint-disable-next-line react/forbid-prop-types
  dirtyFields: PropTypes.object,
  isDirty: PropTypes.bool,
  setValue: PropTypes.func.isRequired,
  // eslint-disable-next-line react/forbid-prop-types
  liquidation: PropTypes.object,
  // eslint-disable-next-line react/forbid-prop-types
  appendixFilesDelete: PropTypes.array,
  setAppendixFilesDelete: PropTypes.func.isRequired,
  isCompleted: PropTypes.bool.isRequired,
  readOnly: PropTypes.bool,
};

LiquidationForm.defaultProps = {
  dirtyFields: {},
  isDirty: false,
  footerConfirmationPrompt: () => null,
  // data: {},
  liquidation: {},
  appendixFilesDelete: [],
  readOnly: false,
};

export default LiquidationForm;
